import {
  type ApolloClient,
  type NormalizedCacheObject,
  useApolloClient,
} from '@apollo/client';
import type { JobDisplayTag } from '@seek/ca-graphql-schema/types';
import type { JobTagType } from '@seek/chalice-types';
import { mapJobTagsToBadges } from '@seek/seek-jobs-analytics';
import { useCallback } from 'react';

import { useSplitViewContext } from 'src/handlers/SplitViewContext';
import getRecommendedBadgesForTracking from 'src/hooks/getRecommendedJobAnalyticsBadges';
import {
  getIsAddSalaryPrefNudgeShown,
  getIsSalaryMatch,
} from 'src/hooks/trackJobDetailsLoaded';
import { useSolHash } from 'src/hooks/useSolHash';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { useIsHomepageRecsBadgingStrongApplicantEnabled } from 'src/store/featureFlags/hooks';
import type {
  JobDetails,
  PersonalisedJobDetails,
} from 'src/store/jobdetails/types';
import { useSelector } from 'src/store/react';
import {
  selectAuthenticated,
  selectLocation,
  selectSearchQuery,
  selectSolMetadata,
} from 'src/store/selectors';
import { getViewJobOriginRef } from 'src/utils/eventCapture/eventCaptureUtils';
import { mapDisplayTagsType } from 'src/utils/mapJobTagsType';
import { useMelwaysLocale } from 'src/utils/melwaysHelper';

import {
  RECOMMENDED_JOB_AI_VIEW_ORIGIN,
  RECOMMENDED_JOB_VIEW_ORIGIN,
} from '../HomePage/Dashboard/SignedInDashboard/DashboardRecommendations/RecommendedJobCard';

interface Options {
  jobDetails: JobDetails;
  isHirerVerified: boolean;
  jobDetailsPersonalised?: PersonalisedJobDetails;
  solMetadataReference?: string;
  isMatchedSkillsWidgetShown?: boolean;
  isMatchedSkillsNudgeShown?: boolean;
  jobOrigin?: string;
}

type UseApplyTracking = (options: Options) => [string, () => void];

const HOME_RECS_ALLOWED_ORIGIN = [
  RECOMMENDED_JOB_VIEW_ORIGIN,
  RECOMMENDED_JOB_AI_VIEW_ORIGIN,
];

const useJobBadgesTracking = (
  jobId: string,
  jobOrigin: string | undefined,
): JobTagType[] => {
  const selectedJob = useSplitViewContext()?.selectedJob;
  const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const location = useSelector(selectLocation);
  const locale = useMelwaysLocale();
  const isHomepageRecsBadgingStrongApplicantEnabled =
    useIsHomepageRecsBadgingStrongApplicantEnabled();

  /**
   * Only applicable if user is in splitView since currently
   * there is no way to track jobTags if user is in standalone
   * job details page.
   */
  if (selectedJob) {
    return mapJobTagsToBadges(selectedJob.tags);
  }

  return [location.query?.ref, jobOrigin].some((target) =>
    HOME_RECS_ALLOWED_ORIGIN.includes(target),
  )
    ? getRecommendedBadgesForTracking({
        apolloClient,
        locale,
        jobId,
        isHomepageRecsBadgingStrongApplicantEnabled,
      })
    : [];
};

const useJdvBadgesTracking = ({
  hasTopApplicantBadge,
  displayTags,
}: {
  hasTopApplicantBadge: boolean;
  displayTags?: JobDisplayTag[];
}) => {
  const topApplicantBadge: Array<'TOP_APPLICANT'> = hasTopApplicantBadge
    ? ['TOP_APPLICANT']
    : [];
  return [...topApplicantBadge, ...mapDisplayTagsType(displayTags)];
};

export const useApplyTracking: UseApplyTracking = ({
  jobDetails,
  jobDetailsPersonalised,
  solMetadataReference,
  isHirerVerified,
  isMatchedSkillsWidgetShown,
  isMatchedSkillsNudgeShown,
  jobOrigin,
}) => {
  const analyticsFacade = useAnalyticsFacade();
  const isAuthenticated = useSelector(selectAuthenticated);
  const solMetadata = useSelector(selectSolMetadata);
  const searchParams = useSelector(selectSearchQuery);

  const { sourcr } = jobDetails;
  const { id, isLinkOut, salary, products } = jobDetails?.job || {};
  const [urlStr, solMeta] = useSolHash({
    id,
    solMetadataReference,
  });

  const salaryMatch = jobDetailsPersonalised?.salaryMatch;
  const isSalaryMatch = getIsSalaryMatch(salaryMatch);
  const searchRemoteOptions = searchParams?.workarrangement;
  const isAddSalaryPrefNudgeShown = getIsAddSalaryPrefNudgeShown({
    isAuthenticated,
    isSalaryLabel: Boolean(salary?.label),
    salaryMatch,
  });

  const jobTags = useJobBadgesTracking(id, jobOrigin);
  const jdvDisplayTags = useJdvBadgesTracking({
    hasTopApplicantBadge: Boolean(jobDetailsPersonalised?.topApplicantBadge),
    displayTags: jobDetails?.job?.products?.displayTags,
  });

  const experimentFlightId = solMetadata?.tags?.mordor__flights ?? '';

  const onApplyClick = useCallback(() => {
    /**
     * If there is no ref query in the URL and the user is
     * in splitView then it is guaranteed the user clicked
     * apply from split view SERP.
     */
    const viewJobOriginRef = getViewJobOriginRef();
    const ref = jobOrigin || viewJobOriginRef || 'search-split';

    analyticsFacade.jobApplyClicked(
      {
        experimentFlightId,
        href: urlStr,
        jobBadges: jobTags,
        jobBadgesShown: jdvDisplayTags,
        jobViewOriginQuery: ref,
        isHirerVerified,
        isMatchedSkillsWidgetShown,
        isMatchedSkillsNudgeShown,
        jobBadgesFilter: searchParams?.tags ? [searchParams?.tags] : [],
        jobCardSellPointCount: products?.bullets?.length,
        hasSourcr: Boolean(sourcr),
      },
      {
        isAddSalaryPrefNudgeShown,
        isSalaryMatch,
        searchRemoteOptions,
        jobBadges: jobTags,
        jobBadgesShown: jdvDisplayTags,
        jobId: id,
        linkout: isLinkOut,
        solMetadata: solMeta,
        thirdPartyLink: isLinkOut,
        isMatchedSkillsWidgetShown,
        isMatchedSkillsNudgeShown,
      },
    );
  }, [
    jobOrigin,
    analyticsFacade,
    experimentFlightId,
    urlStr,
    isHirerVerified,
    isMatchedSkillsWidgetShown,
    isMatchedSkillsNudgeShown,
    isAddSalaryPrefNudgeShown,
    isSalaryMatch,
    searchRemoteOptions,
    id,
    isLinkOut,
    solMeta,
    jobTags,
    jdvDisplayTags,
    searchParams?.tags,
    products?.bullets,
    sourcr,
  ]);

  return [urlStr, onApplyClick];
};
